<template>
    <div class="search-header">
        <DSInput
            id="sl-search"
            ref="searchInput"
            v-model="searchQuery"
            class="search-input"
            :label="$t('searchStation')"
            :icon-after="searchInputIcon"
            :on-click-after="reset"
            @update:model-value="value => doSearch = true"
            @blur="emit('blur')"
        />
        <hr />
        <ul class="quick-filter-list">
            <li
                v-for="(item, key) in quickFilters"
                :key="`quick-filter-${key}`"
            >
                <button
                    :class="{
                        'selected': isSelected(item.code),
                    }"
                    @click="$emit('select', item.code)"
                >
                    <DSFontIcon :code="item.quickFilterIcon ?? item.icon" size="xxxxl" />
                    <span class="label">{{ item.label ? item.label : $t(`labels.${item.code}`) }}</span>
                </button>
            </li>
        </ul>
    </div>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useI18n } from '#imports';

const dataSearchQuery = ref('');
const debounceTimeout = ref(null);

const props = defineProps({
    selected: {
        type: Array,
        required: false,
        default: () => [],
    },
    filters: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const emit = defineEmits(['input', 'select', 'blur']);

const quickFilters = computed(() => {
    const result = [];
    for (const category in props.filters) {
        const quickFiltersPerCategory = props.filters[category]?.filters?.filter(filter => filter.quickFilter) || [];
        if (quickFiltersPerCategory.length >= 1) {
            result.push(...quickFiltersPerCategory.map(filter => ({ ...filter, category })));
        }
    }

    result.sort((a, b) => a.quickFilterPosition - b.quickFilterPosition);

    return result;
});

const searchQuery = computed({
    get: () => dataSearchQuery.value,
    set: value => {
        dataSearchQuery.value = value;

        if (debounceTimeout.value) {
            clearTimeout(debounceTimeout.value);
        }

        debounceTimeout.value = setTimeout(() => {
            emit('input', dataSearchQuery.value);
        }, 250);
    },
});

const searchInputIcon = computed(() => ({
    code: searchQuery.value.length > 0 ? 'delete-text' : 'search',
}));

const isSelected = code => {
    if (typeof(code) === 'object') {
        return props.selected.map(item => JSON.stringify(item)).includes(JSON.stringify(code));
    }

    return props.selected.includes(code);
};

const reset = () => {
    searchQuery.value = '';
};

useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            searchStation: 'Search stations',
        },
        'fi-FI': {
            searchStation: 'Hae asemaa',
            labels: {
                helmisimpukkarestaurant: 'Ravintola',
                helmisimpukka: 'Express',
                open24h: 'Avoinna 24h',
                lunch: 'Lounas',
            },
        },
        'sv-SE': {
            searchStation: 'Sök station',
        },
        'no-NO': {
            searchStation: 'Søk stasjon',
        },
    },
});

</script>
<style scoped>
.quick-filter-list {
  list-style: none;
  margin: 0;
  margin-top: 0.5rem;
  padding: 0 1.5rem;
  display: grid;
  align-items: start;
  justify-items: center;
  grid-gap: 0.25rem;
  grid-template-columns: repeat(4, calc(25% - 0.1875rem));
  border-bottom: 1px solid var(--color-base-grey-200);
}

button {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 3rem auto;
  justify-items: center;
  align-items: center;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
button .icon {
  transition: all 0.2s ease-in-out;
  color: var(--color-base-grey-800);
}
button .label {
  display: inline-flex;
  white-space: wrap;
  position: relative;
  font-size: 0.625rem;
  margin-top: 0.5rem;
  min-height: 2.2rem;
  line-height: 1.25em;
  padding-bottom: 0.25rem;
}
button .label::after {
  transition: all 0.2s ease-in-out;
  content: "";
  position: absolute;
  height: 0.25rem;
  width: 100%;
  left: 0;
  bottom: 0;
  background-color: var(--color-primary-500);
  transform-origin: center bottom;
  transform: scaleY(0);
}
button:hover .icon {
  color: var(--color-base-grey-500);
}
button.selected .icon {
  color: var(--color-primary-500);
}
button.selected .label::after {
  transform: scaleY(1);
}

hr {
  margin-block: 0.75rem;
}

.search-input {
  margin: 0.75rem 0.75rem 0;
}
</style>
